/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  CircleMarker,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Shield,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  CircleDot,
  CircleOff,
  BatteryFull,
  BatteryMedium,
  BatteryLow,
  BatteryWarning,
  Crosshair,
  Clock,
  UserPlus,
  Pencil,
  X,
  CalendarPlus,
  Trash2,
} from "lucide-react";
import { securityApi } from "./api";
import "./SupervisorMap.css";

// react-leaflet ships the default marker icon as a bundled asset. Under
// CRA the default URLs break because webpack rewrites them; the standard
// workaround is to point them at the CDN copy. The villa community spans
// a small area, so a simple marker is fine.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Fall back to a central Hyderabad point when we have no pings yet — the
// villa community lives there. Zoom 16 frames a single sub-area.
const DEFAULT_CENTER = [17.385, 78.4867];
const DEFAULT_ZOOM = 16;
const MAP_REFRESH_MS = 15000;
const ONDUTY_REFRESH_MS = 15000;

function accuracyColor(acc) {
  if (acc == null) return "#64748b";
  if (acc <= 15) return "#16a34a"; // green
  if (acc <= 40) return "#d97706"; // amber
  return "#dc2626"; // red
}

function roleLabel(role) {
  const r = String(role || "").toUpperCase();
  if (r === "SUPERVISOR") return "Supervisor";
  if (r === "GUARD") return "Guard";
  return role || "Guard";
}

function formatIST(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: false,
    });
  } catch (_) {
    return iso;
  }
}

function formatISTTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch (_) {
    return iso;
  }
}

function secsSince(iso) {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}

function secsLabel(secs) {
  if (secs == null) return null;
  if (secs < 60) return `${secs}s ago`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m} min ago`;
  return `${Math.floor(m / 60)} hr ago`;
}

function batteryColor(pct) {
  if (pct == null) return "#94a3b8";
  if (pct > 50) return "#16a34a";
  if (pct > 20) return "#d97706";
  return "#dc2626";
}

function BatteryIcon({ pct, size = 13 }) {
  if (pct == null) return <BatteryWarning size={size} color="#94a3b8" />;
  if (pct > 70) return <BatteryFull size={size} color={batteryColor(pct)} />;
  if (pct > 35) return <BatteryMedium size={size} color={batteryColor(pct)} />;
  if (pct > 15) return <BatteryLow size={size} color={batteryColor(pct)} />;
  return <BatteryWarning size={size} color={batteryColor(pct)} />;
}

// Re-center the map when the selected guard's latest ping changes.
function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom(), { animate: true });
    }
  }, [center && center[0], center && center[1]]);
  return null;
}

export default function SupervisorMap() {
  // Roster (with on-duty dot)
  const [guards, setGuards] = useState([]);
  const [guardsErr, setGuardsErr] = useState("");
  const [guardsLoading, setGuardsLoading] = useState(true);

  // Live on-duty list — refreshed every 15s, used to draw markers for
  // every guard currently on shift.
  const [onDuty, setOnDuty] = useState([]); // [{guard_id, name, role, ...}]

  // Latest known ping per guard_id, keyed by id.
  // { [guardId]: { lat, lng, accuracy_m, recorded_at } }
  const [latestPings, setLatestPings] = useState({});

  // Selected guard's full day's track + ping rows.
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState([]);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedErr, setSelectedErr] = useState("");

  const [tableOpen, setTableOpen] = useState(false);

  // Add-guard modal state
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addRole, setAddRole] = useState("GUARD");
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");

  const resetAddForm = () => {
    setAddName("");
    setAddPhone("");
    setAddRole("GUARD");
    setAddError("");
    setAddSuccess("");
  };

  // Edit-guard modal state (role change)
  const [editGuard, setEditGuard] = useState(null); // guard object or null
  const [editRole, setEditRole] = useState("GUARD");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");

  const openEditGuard = (g) => {
    setEditGuard(g);
    setEditRole(g.role || "GUARD");
    setEditError("");
  };

  const submitEditGuard = async () => {
    if (!editGuard) return;
    setEditSubmitting(true);
    setEditError("");
    try {
      await securityApi.patchGuard(editGuard.guard_id, { role: editRole });
      setEditGuard(null);
      await loadGuards();
    } catch (e) {
      setEditError(e?.message || "Could not update guard.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const submitAddGuard = async () => {
    setAddError("");
    setAddSuccess("");
    const name = addName.trim();
    const digits = addPhone.replace(/\D/g, "");
    if (!name) { setAddError("Please enter a name."); return; }
    if (digits.length !== 10) { setAddError("Phone must be a 10-digit number."); return; }
    setAddSubmitting(true);
    try {
      await securityApi.createGuard({ name, phone_number: digits, role: addRole });
      setAddSuccess(`OTP sent — ${name} can now log in.`);
      resetAddForm();
      await loadGuards();
    } catch (e) {
      setAddError(e?.message || "Could not add guard.");
    } finally {
      setAddSubmitting(false);
    }
  };

  // ---- Shift scheduling ----
  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsErr, setShiftsErr] = useState("");
  const [shiftsOpen, setShiftsOpen] = useState(false);

  // Add-shift modal
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [shiftGuardId, setShiftGuardId] = useState("");
  const [shiftDate, setShiftDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [shiftStart, setShiftStart] = useState("08:00");
  const [shiftEnd, setShiftEnd] = useState("20:00");
  const [shiftArea, setShiftArea] = useState("");
  const [shiftSubmitting, setShiftSubmitting] = useState(false);
  const [shiftError, setShiftError] = useState("");
  const [deletingShiftId, setDeletingShiftId] = useState(null);

  const loadShifts = useCallback(async () => {
    setShiftsLoading(true);
    setShiftsErr("");
    try {
      const data = await securityApi.listShifts();
      setShifts(Array.isArray(data) ? data : []);
    } catch (e) {
      setShiftsErr(e?.message || "Could not load shifts");
    } finally {
      setShiftsLoading(false);
    }
  }, []);

  const submitShift = async () => {
    setShiftError("");
    if (!shiftGuardId) { setShiftError("Select a guard."); return; }
    const starts_at = new Date(`${shiftDate}T${shiftStart}:00`).toISOString();
    const ends_at = new Date(`${shiftDate}T${shiftEnd}:00`).toISOString();
    if (new Date(ends_at) <= new Date(starts_at)) {
      setShiftError("End time must be after start time.");
      return;
    }
    setShiftSubmitting(true);
    try {
      await securityApi.createShift({
        guard_id: shiftGuardId,
        starts_at,
        ends_at,
        assigned_area: shiftArea.trim() || null,
      });
      setShiftModalOpen(false);
      setShiftGuardId("");
      setShiftArea("");
      setShiftError("");
      await loadShifts();
    } catch (e) {
      setShiftError(e?.message || "Could not create shift.");
    } finally {
      setShiftSubmitting(false);
    }
  };

  const deleteShift = async (shiftId) => {
    if (deletingShiftId) return;
    setDeletingShiftId(shiftId);
    try {
      await securityApi.deleteShift(shiftId);
      setShifts((prev) => prev.filter((s) => s.id !== shiftId));
    } catch (e) {
      alert(e?.message || "Could not delete shift.");
    } finally {
      setDeletingShiftId(null);
    }
  };

  // Tracks the last-fetched server timestamp per guard so the periodic
  // refresh only pulls new pings.
  const lastFetchRef = useRef({}); // { [guardId]: ISO }
  const abortersRef = useRef({}); // { [key]: AbortController }

  // ---- helpers ----
  const cancelAborter = useCallback((key) => {
    const a = abortersRef.current[key];
    if (a) {
      try {
        a.abort();
      } catch (_) {}
      delete abortersRef.current[key];
    }
  }, []);

  // ---- Guards roster ----
  const loadGuards = useCallback(async () => {
    cancelAborter("guards");
    const ctrl = new AbortController();
    abortersRef.current["guards"] = ctrl;
    try {
      const data = await securityApi.listGuards({ signal: ctrl.signal });
      setGuards(Array.isArray(data) ? data : []);
      setGuardsErr("");
    } catch (e) {
      if (e.name !== "AbortError") setGuardsErr(e?.message || "Could not load guards");
    } finally {
      setGuardsLoading(false);
    }
  }, [cancelAborter]);

  // ---- On-duty markers + latest pings ----
  const loadOnDutyAndMarkers = useCallback(async () => {
    cancelAborter("onduty");
    const ctrl = new AbortController();
    abortersRef.current["onduty"] = ctrl;
    let duty;
    try {
      duty = await securityApi.listOnDuty({ signal: ctrl.signal });
    } catch (e) {
      if (e.name !== "AbortError") {
        // Soft-fail — keep prior markers.
        // eslint-disable-next-line no-console
        console.warn("on-duty fetch failed:", e?.message);
      }
      return;
    }
    if (!Array.isArray(duty)) duty = [];
    setOnDuty(duty);

    // Prune cached pings for guards no longer on duty so stale markers
    // don't linger on the map.
    const dutyIds = new Set(duty.map((g) => g.guard_id));
    setLatestPings((prev) => {
      const next = {};
      for (const id of Object.keys(prev)) {
        if (dutyIds.has(id)) next[id] = prev[id];
      }
      return next;
    });

    const now = new Date().toISOString();
    // Fetch only the recent slice for each on-duty guard.
    await Promise.all(
      duty.map(async (g) => {
        const key = `marker:${g.guard_id}`;
        cancelAborter(key);
        const c = new AbortController();
        abortersRef.current[key] = c;
        const from =
          lastFetchRef.current[g.guard_id] ||
          new Date(Date.now() - 5 * 60 * 1000).toISOString();
        try {
          const rows = await securityApi.getTrack(g.guard_id, {
            from,
            to: now,
            signal: c.signal,
          });
          lastFetchRef.current[g.guard_id] = now;
          if (Array.isArray(rows) && rows.length > 0) {
            const last = rows[rows.length - 1];
            setLatestPings((prev) => ({
              ...prev,
              [g.guard_id]: {
                lat: last.lat,
                lng: last.lng,
                accuracy_m: last.accuracy_m,
                battery_pct: last.battery_pct ?? null,
                recorded_at: last.recorded_at,
              },
            }));
          }
        } catch (e) {
          if (e.name !== "AbortError") {
            // eslint-disable-next-line no-console
            console.warn("track fetch failed:", g.guard_id, e?.message);
          }
        }
      })
    );
  }, [cancelAborter]);

  // ---- Selected guard full-day track ----
  const loadSelectedTrack = useCallback(
    async (guardId) => {
      if (!guardId) {
        setSelectedTrack([]);
        return;
      }
      cancelAborter("selected");
      const ctrl = new AbortController();
      abortersRef.current["selected"] = ctrl;
      setSelectedLoading(true);
      setSelectedErr("");
      try {
        const rows = await securityApi.getTrack(guardId, {
          signal: ctrl.signal,
        });
        setSelectedTrack(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (e.name !== "AbortError") {
          setSelectedErr(e?.message || "Could not load track");
          setSelectedTrack([]);
        }
      } finally {
        setSelectedLoading(false);
      }
    },
    [cancelAborter]
  );

  // ---- Effects ----
  useEffect(() => {
    loadGuards();
    loadOnDutyAndMarkers();
    const t1 = setInterval(loadOnDutyAndMarkers, MAP_REFRESH_MS);
    return () => {
      clearInterval(t1);
      // Abort every in-flight request on unmount.
      Object.keys(abortersRef.current).forEach((k) => cancelAborter(k));
    };
  }, [loadGuards, loadOnDutyAndMarkers, cancelAborter]);

  useEffect(() => {
    // Reset selected-track aborter when guard changes.
    cancelAborter("selected");
    loadSelectedTrack(selectedId);
  }, [selectedId, loadSelectedTrack, cancelAborter]);

  const togglingRef = useRef({});
  const toggleActive = async (g) => {
    if (togglingRef.current[g.guard_id]) return;
    togglingRef.current[g.guard_id] = true;
    try {
      await securityApi.patchGuard(g.guard_id, { is_active: !g.is_active });
      await loadGuards();
    } catch (e) {
      alert(e?.message || "Could not update guard");
    } finally {
      togglingRef.current[g.guard_id] = false;
    }
  };

  // ---- Derived view state ----
  const selectedGuard = useMemo(
    () => guards.find((g) => g.guard_id === selectedId) || null,
    [guards, selectedId]
  );

  const trackLatLngs = useMemo(
    () =>
      selectedTrack
        .filter((p) => typeof p.lat === "number" && typeof p.lng === "number")
        .map((p) => [p.lat, p.lng]),
    [selectedTrack]
  );

  const mapCenter = useMemo(() => {
    // If a guard is selected and we have a latest ping, centre on it.
    if (selectedId && latestPings[selectedId]) {
      const p = latestPings[selectedId];
      return [p.lat, p.lng];
    }
    if (selectedId && selectedTrack.length > 0) {
      const last = selectedTrack[selectedTrack.length - 1];
      return [last.lat, last.lng];
    }
    return DEFAULT_CENTER;
  }, [selectedId, latestPings, selectedTrack]);

  return (
    <div className="vssec-map-shell">
      <div className="vssec-map-head">
        <div>
          <h1>
            <Shield size={20} strokeWidth={2} /> Security supervisor
          </h1>
          <p className="vssec-map-sub">
            Live positions of on-duty guards, plus the full day's track for
            whoever you select on the left. Refreshes every 15 seconds.
          </p>
        </div>
        <button
          className="vssec-map-refresh"
          onClick={() => {
            loadGuards();
            loadOnDutyAndMarkers();
            if (selectedId) loadSelectedTrack(selectedId);
          }}
          title="Refresh now"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="vssec-map-grid">
        {/* Left pane: roster */}
        <aside className="vssec-map-roster">
          <div className="vssec-map-roster-head">
            <span>Guards</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="vssec-map-roster-count">{guards.length}</span>
              <button
                type="button"
                className="vssec-map-add-guard-btn"
                onClick={() => { resetAddForm(); setAddOpen(true); }}
                title="Add guard"
              >
                <UserPlus size={13} /> Add
              </button>
            </div>
          </div>
          {guardsErr && (
            <div className="vssec-map-error">{guardsErr}</div>
          )}
          {guardsLoading && !guards.length && (
            <div className="vssec-map-roster-empty">Loading guards…</div>
          )}
          {!guardsLoading && guards.length === 0 && (
            <div className="vssec-map-roster-empty">
              No guards onboarded yet. Click "Add" to get started.
            </div>
          )}
          <ul className="vssec-map-roster-list">
            {guards.map((g) => {
              const isSelected = g.guard_id === selectedId;
              return (
                <li
                  key={g.guard_id}
                  className={`vssec-map-roster-row ${isSelected ? "selected" : ""}`}
                  onClick={() => setSelectedId(g.guard_id)}
                >
                  <div className="vssec-map-roster-row-main">
                    <span
                      className={`vssec-map-dot ${g.on_duty_now ? "on" : "off"}`}
                      title={g.on_duty_now ? "On duty now" : "Off duty"}
                    />
                    <div className="vssec-map-roster-text">
                      <div className="vssec-map-name">{g.name || "Guard"}</div>
                      <div className="vssec-map-rolepill">
                        {roleLabel(g.role)}
                      </div>
                      {(() => {
                        const p = latestPings[g.guard_id];
                        if (!p) return null;
                        const secs = secsSince(p.recorded_at);
                        return (
                          <div className="vssec-map-guard-status">
                            <span
                              className="vssec-map-status-chip acc"
                              style={{ color: accuracyColor(p.accuracy_m) }}
                              title={`GPS accuracy ${Math.round(p.accuracy_m)}m`}
                            >
                              <Crosshair size={10} />
                              {Math.round(p.accuracy_m)}m
                            </span>
                            <span
                              className="vssec-map-status-chip bat"
                              style={{ color: batteryColor(p.battery_pct) }}
                              title={p.battery_pct != null ? `Battery ${p.battery_pct}%` : "Battery unknown"}
                            >
                              <BatteryIcon pct={p.battery_pct} size={11} />
                              {p.battery_pct != null ? `${p.battery_pct}%` : "—"}
                            </span>
                            <span
                              className="vssec-map-status-chip sync"
                              title={`Last ping: ${formatIST(p.recorded_at)}`}
                            >
                              <Clock size={10} />
                              {secsLabel(secs)}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      className={`vssec-map-toggle ${g.is_active ? "active" : "inactive"}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActive(g);
                      }}
                      title={
                        g.is_active
                          ? "Active — click to deactivate"
                          : "Inactive — click to activate"
                      }
                    >
                      {g.is_active ? (
                        <>
                          <CircleDot size={12} /> Active
                        </>
                      ) : (
                        <>
                          <CircleOff size={12} /> Inactive
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="vssec-map-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditGuard(g);
                      }}
                      title="Edit role"
                    >
                      <Pencil size={11} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Center: map */}
        <section className="vssec-map-canvas">
          <MapContainer
            center={mapCenter}
            zoom={DEFAULT_ZOOM}
            scrollWheelZoom
            className="vssec-leaflet-root"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Recenter center={mapCenter} zoom={DEFAULT_ZOOM} />

            {/* One marker per currently on-duty guard */}
            {onDuty.map((g) => {
              const p = latestPings[g.guard_id];
              if (!p) return null;
              return (
                <Marker
                  key={`m-${g.guard_id}`}
                  position={[p.lat, p.lng]}
                  eventHandlers={{
                    click: () => setSelectedId(g.guard_id),
                  }}
                >
                  <Popup>
                    <div className="vssec-map-popup">
                      <strong>{g.name || "Guard"}</strong>
                      <div>{roleLabel(g.role)}</div>
                      {g.assigned_area && <div>{g.assigned_area}</div>}
                      <div className="vssec-map-popup-meta">
                        Accuracy {Math.round(p.accuracy_m)}m · seen{" "}
                        {formatISTTime(p.recorded_at)} IST
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Latest-ping accuracy dot (coloured) per on-duty guard */}
            {onDuty.map((g) => {
              const p = latestPings[g.guard_id];
              if (!p) return null;
              return (
                <CircleMarker
                  key={`c-${g.guard_id}`}
                  center={[p.lat, p.lng]}
                  radius={9}
                  pathOptions={{
                    color: accuracyColor(p.accuracy_m),
                    fillColor: accuracyColor(p.accuracy_m),
                    fillOpacity: 0.55,
                    weight: 2,
                  }}
                />
              );
            })}

            {/* Selected guard's full-day polyline */}
            {trackLatLngs.length > 1 && (
              <Polyline
                positions={trackLatLngs}
                pathOptions={{ color: "var(--accent)", weight: 4, opacity: 0.7 }}
              />
            )}
          </MapContainer>

          {selectedGuard && (
            <div className="vssec-map-selected-bar">
              <div>
                <strong>{selectedGuard.name || "Guard"}</strong> ·{" "}
                {roleLabel(selectedGuard.role)} · {selectedTrack.length} pings
                today
              </div>
              {selectedLoading && <span>Loading track…</span>}
              {selectedErr && <span className="vssec-err">{selectedErr}</span>}
            </div>
          )}
        </section>
      </div>

      {/* Shift scheduling panel */}
      <div className="vssec-shifts">
        <button
          className="vssec-shifts-toggle"
          onClick={() => {
            if (!shiftsOpen) loadShifts();
            setShiftsOpen((v) => !v);
          }}
        >
          {shiftsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span>Today's shifts ({shifts.length})</span>
          <button
            type="button"
            className="vssec-shifts-add-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!shiftsOpen) { loadShifts(); setShiftsOpen(true); }
              setShiftDate(new Date().toISOString().split("T")[0]);
              setShiftError("");
              setShiftModalOpen(true);
            }}
            title="Add shift"
          >
            <CalendarPlus size={13} /> Add shift
          </button>
        </button>

        {shiftsOpen && (
          <div className="vssec-shifts-body">
            {shiftsErr && <div className="vssec-map-error">{shiftsErr}</div>}
            {shiftsLoading && <div className="vssec-shifts-empty">Loading…</div>}
            {!shiftsLoading && shifts.length === 0 && (
              <div className="vssec-shifts-empty">No shifts scheduled today.</div>
            )}
            {!shiftsLoading && shifts.length > 0 && (
              <table className="vssec-shifts-table">
                <thead>
                  <tr>
                    <th>Guard</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Area</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((s) => {
                    const guard = guards.find((g) => g.guard_id === s.guard_id);
                    return (
                      <tr key={s.id}>
                        <td>{guard?.name || s.guard_id.slice(0, 8)}</td>
                        <td>{formatISTTime(s.starts_at)}</td>
                        <td>{formatISTTime(s.ends_at)}</td>
                        <td>{s.assigned_area || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="vssec-shifts-del"
                            disabled={deletingShiftId === s.id}
                            onClick={() => deleteShift(s.id)}
                            title="Delete shift"
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Add-shift modal */}
      {shiftModalOpen && (
        <div className="vssec-modal-backdrop" onClick={() => setShiftModalOpen(false)}>
          <div className="vssec-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vssec-modal-head">
              <h2><CalendarPlus size={17} /> Add shift</h2>
              <button type="button" className="vssec-modal-close" onClick={() => setShiftModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="vssec-modal-body">
              <label className="vssec-modal-label">
                Guard
                <select
                  className="vssec-modal-input"
                  value={shiftGuardId}
                  onChange={(e) => setShiftGuardId(e.target.value)}
                >
                  <option value="">— select guard —</option>
                  {guards.filter((g) => g.is_active).map((g) => (
                    <option key={g.guard_id} value={g.guard_id}>
                      {g.name || g.guard_id.slice(0, 8)} ({roleLabel(g.role)})
                    </option>
                  ))}
                </select>
              </label>
              <label className="vssec-modal-label">
                Date
                <input
                  type="date"
                  className="vssec-modal-input"
                  value={shiftDate}
                  onChange={(e) => setShiftDate(e.target.value)}
                />
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label className="vssec-modal-label">
                  Start time
                  <input
                    type="time"
                    className="vssec-modal-input"
                    value={shiftStart}
                    onChange={(e) => setShiftStart(e.target.value)}
                  />
                </label>
                <label className="vssec-modal-label">
                  End time
                  <input
                    type="time"
                    className="vssec-modal-input"
                    value={shiftEnd}
                    onChange={(e) => setShiftEnd(e.target.value)}
                  />
                </label>
              </div>
              <label className="vssec-modal-label">
                Area (optional)
                <input
                  className="vssec-modal-input"
                  value={shiftArea}
                  onChange={(e) => setShiftArea(e.target.value)}
                  placeholder="e.g. Block A gate"
                />
              </label>
              {shiftError && <div className="vssec-modal-error">{shiftError}</div>}
            </div>
            <div className="vssec-modal-footer">
              <button type="button" className="vssec-modal-btn vssec-modal-btn--ghost" onClick={() => setShiftModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="vssec-modal-btn vssec-modal-btn--primary"
                disabled={shiftSubmitting}
                onClick={submitShift}
              >
                {shiftSubmitting ? "Saving…" : "Create shift"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add guard modal */}
      {addOpen && (
        <div className="vssec-modal-backdrop" onClick={() => setAddOpen(false)}>
          <div className="vssec-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vssec-modal-head">
              <h2><UserPlus size={17} /> Add guard</h2>
              <button
                type="button"
                className="vssec-modal-close"
                onClick={() => setAddOpen(false)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="vssec-modal-body">
              <label className="vssec-modal-label">
                Name
                <input
                  className="vssec-modal-input"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="Full name"
                  autoFocus
                />
              </label>

              <label className="vssec-modal-label">
                Phone number
                <input
                  className="vssec-modal-input"
                  value={addPhone}
                  onChange={(e) => setAddPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit Indian number"
                  inputMode="numeric"
                />
              </label>

              <div className="vssec-modal-label">
                Role
                <div className="vssec-modal-segmented">
                  {["GUARD", "SUPERVISOR"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={`vssec-modal-seg ${addRole === opt ? "active" : ""}`}
                      onClick={() => setAddRole(opt)}
                    >
                      {opt === "GUARD" ? "Guard" : "Supervisor"}
                    </button>
                  ))}
                </div>
              </div>

              {addError && <div className="vssec-modal-error">{addError}</div>}
              {addSuccess && <div className="vssec-modal-success">{addSuccess}</div>}
            </div>

            <div className="vssec-modal-footer">
              <button
                type="button"
                className="vssec-modal-btn vssec-modal-btn--ghost"
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="vssec-modal-btn vssec-modal-btn--primary"
                disabled={addSubmitting}
                onClick={submitAddGuard}
              >
                {addSubmitting ? "Sending OTP…" : "Send OTP & add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit-guard modal (role change) */}
      {editGuard && (
        <div className="vssec-modal-backdrop" onClick={() => setEditGuard(null)}>
          <div className="vssec-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vssec-modal-head">
              <h2><Pencil size={17} /> Edit guard — {editGuard.name || "Guard"}</h2>
              <button type="button" className="vssec-modal-close" onClick={() => setEditGuard(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="vssec-modal-body">
              <div className="vssec-modal-label">
                Role
                <div className="vssec-modal-segmented">
                  {["GUARD", "SUPERVISOR"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={`vssec-modal-seg ${editRole === opt ? "active" : ""}`}
                      onClick={() => setEditRole(opt)}
                    >
                      {opt === "GUARD" ? "Guard" : "Supervisor"}
                    </button>
                  ))}
                </div>
              </div>
              {editError && <div className="vssec-modal-error">{editError}</div>}
            </div>
            <div className="vssec-modal-footer">
              <button type="button" className="vssec-modal-btn vssec-modal-btn--ghost" onClick={() => setEditGuard(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="vssec-modal-btn vssec-modal-btn--primary"
                disabled={editSubmitting || editRole === (editGuard.role || "GUARD")}
                onClick={submitEditGuard}
              >
                {editSubmitting ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ping table */}
      {selectedGuard && (
        <div className="vssec-map-pings">
          <button
            className="vssec-map-pings-toggle"
            onClick={() => setTableOpen((v) => !v)}
          >
            {tableOpen ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
            <span>
              Ping log for {selectedGuard.name || "guard"} ({selectedTrack.length}{" "}
              points)
            </span>
          </button>
          {tableOpen && (
            <div className="vssec-map-pings-tablewrap">
              <table className="vssec-map-pings-table">
                <thead>
                  <tr>
                    <th>Time (IST)</th>
                    <th>Latitude</th>
                    <th>Longitude</th>
                    <th>Accuracy (m)</th>
                    <th>Battery</th>
                    <th>On duty</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTrack.length === 0 && !selectedLoading && (
                    <tr>
                      <td colSpan={6} className="vssec-map-pings-empty">
                        No pings for this guard today.
                      </td>
                    </tr>
                  )}
                  {selectedTrack.map((p, i) => (
                    <tr key={`${p.recorded_at}-${i}`}>
                      <td>{formatIST(p.recorded_at)}</td>
                      <td>{p.lat?.toFixed(6)}</td>
                      <td>{p.lng?.toFixed(6)}</td>
                      <td style={{ color: accuracyColor(p.accuracy_m) }}>
                        {Math.round(p.accuracy_m)}
                      </td>
                      <td style={{ color: batteryColor(p.battery_pct) }}>
                        {p.battery_pct != null ? `${p.battery_pct}%` : "—"}
                      </td>
                      <td>{p.is_on_duty ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
